import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MiniField } from "./scheduleVisitPanelUi";

const SAME_SCHEDULE_TOOLTIP =
  "Share one visit schedule across all treatment groups. Turn off to configure visits per group.";

function InfoTooltip({ text }) {
  const anchorRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const updatePosition = () => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPosition({
      top: rect.bottom + 6,
      left: Math.max(8, rect.left - 4),
    });
  };

  const show = () => {
    updatePosition();
    setVisible(true);
  };

  const hide = () => setVisible(false);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className="schedule-group-selector__info"
        aria-label="Same schedule details"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        <i className="fas fa-info-circle" aria-hidden />
      </button>
      {visible
        ? createPortal(
            <div
              className="schedule-group-selector__tooltip"
              style={{ top: position.top, left: position.left }}
              role="tooltip"
            >
              {text}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

export default function ScheduleGroupSelector({
  options = [],
  value = "",
  onChange,
  allGroupsSameSchedule = true,
  onSameScheduleChange,
  showAdd = false,
  onAdd,
  selectedCohort = null,
  showGroupActions = false,
  onSaveGroupName,
  onDeleteGroup,
  busy = false,
  disabled = false,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [originalName, setOriginalName] = useState("");
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    setIsEditing(false);
    if (selectedCohort && showGroupActions) {
      const name = selectedCohort.name || "";
      setEditName(name);
      setOriginalName(name);
      return;
    }
    setEditName("");
    setOriginalName("");
  }, [selectedCohort?.id, selectedCohort?.name, showGroupActions]);

  const nameUnchanged =
    String(editName || "").trim() === String(originalName || "").trim();

  const cancelEdit = () => {
    setEditName(originalName);
    setIsEditing(false);
  };

  const commitRename = async () => {
    const trimmed = String(editName || "").trim();
    if (!showGroupActions || !selectedCohort?.id || busy || savingName) return;
    if (!trimmed) {
      cancelEdit();
      return;
    }
    if (nameUnchanged) {
      setIsEditing(false);
      return;
    }
    setSavingName(true);
    try {
      await onSaveGroupName?.(selectedCohort.id, trimmed);
      setOriginalName(trimmed);
      setEditName(trimmed);
      setIsEditing(false);
    } catch {
      setEditName(originalName);
    } finally {
      setSavingName(false);
    }
  };

  const handleRenameKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitRename();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      cancelEdit();
    }
  };

  const startEdit = () => {
    if (!showGroupActions || disabled || busy) return;
    setEditName(originalName);
    setIsEditing(true);
  };

  return (
    <div className="schedule-group-selector">
      {onSameScheduleChange ? (
        <div
          className={`schedule-group-selector__toggle-row${
            disabled ? " is-locked" : ""
          }`}
        >
          <InfoTooltip text={SAME_SCHEDULE_TOOLTIP} />
          <label
            htmlFor="schedule-same-groups-toggle"
            className="schedule-group-selector__toggle-label"
          >
            Same schedule for all groups
          </label>
          <div className="form-check form-switch schedule-group-selector__switch m-0">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="schedule-same-groups-toggle"
              checked={!!allGroupsSameSchedule}
              disabled={disabled || busy}
              onChange={(event) => onSameScheduleChange?.(event.target.checked)}
            />
          </div>
        </div>
      ) : null}

      {showAdd ? (
        <button
          type="button"
          className="gvisit-rail-add schedule-group-selector__add"
          disabled={disabled || busy}
          onClick={onAdd}
        >
          {busy ? "Adding…" : "+ Add group"}
        </button>
      ) : null}

      {options.length ? (
        <div className="schedule-group-selector__group-row">
          <div className="schedule-group-selector__group-field">
            <MiniField label="Group">
            {isEditing ? (
              <input
                type="text"
                className="gvisit-rail-item__rename-input schedule-group-selector__name-input"
                value={editName}
                maxLength={100}
                autoFocus
                disabled={disabled || busy || savingName}
                onChange={(event) => setEditName(event.target.value)}
                onKeyDown={handleRenameKeyDown}
              />
            ) : (
              <select
                id="schedule-group-select"
                className="gvisit-select-box"
                value={value}
                disabled={disabled || busy}
                onChange={(event) => onChange?.(event.target.value)}
              >
                {options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            )}
            </MiniField>
          </div>
          {showGroupActions ? (
            <div className="schedule-group-selector__group-actions" aria-label="Group actions">
              {isEditing ? (
                <>
                  <button
                    type="button"
                    className="gvisit-rail-item__action gvisit-rail-item__action--save"
                    title="Save group name"
                    disabled={
                      disabled || busy || savingName || nameUnchanged || !editName.trim()
                    }
                    onClick={commitRename}
                  >
                    <i className="fas fa-check" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="gvisit-rail-item__action"
                    title="Cancel edit"
                    disabled={disabled || busy || savingName}
                    onClick={cancelEdit}
                  >
                    <i className="fas fa-times" aria-hidden />
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="gvisit-rail-item__action"
                    title="Rename group"
                    disabled={disabled || busy}
                    onClick={startEdit}
                  >
                    <i className="fas fa-pencil-alt" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="gvisit-rail-item__action gvisit-rail-item__action--danger"
                    title="Delete group"
                    disabled={disabled || busy}
                    onClick={() =>
                      onDeleteGroup?.(selectedCohort?.id, selectedCohort?.name)
                    }
                  >
                    <i className="fas fa-trash-alt" aria-hidden />
                  </button>
                </>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
